// Sotravel Club design kit — Luma edition. shadcn-shaped primitives (Button,
// Card, Input, Badge, Tabs, Dialog, Sheet, Popover …) in plain React +
// Tailwind 3 with the `tw-` prefix, no Radix except Slot. Tokens live in
// ./club.css and are scoped to `.club-admin`.
//
// Measured on luma.com: 16px form text, pill buttons (16px radius at 30–38px
// height), 8px inputs, 12px cards, 24px sheets, translucent ink fills instead
// of borders, ink primary button.
import React, { createContext, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";
import { AnimatePresence, motion, useDragControls } from "framer-motion";
import dayjs from "dayjs";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import { AlertCircle, AlertTriangle, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, ImagePlus, Info, LoaderCircle, MoreHorizontal, Trash2, X } from "lucide-react";
import { ImageResizable } from "../../components/elements/ImageResizeable";
import { uploadBigFileToS3 } from "../../services/uploads";
import "./club.css";
import { clubDate, clubFormat, clubTime } from "../../utils/clubDates";

const THEME_COLORS = ["background", "foreground", "card", "card-foreground", "popover", "popover-foreground", "primary", "primary-foreground", "secondary", "secondary-foreground", "muted", "muted-foreground", "accent", "accent-foreground", "destructive", "destructive-foreground", "border", "input", "ring"];
const twMerge = extendTailwindMerge({ prefix: "tw-", extend: { theme: { color: THEME_COLORS } } });
export const cn = (...inputs) => twMerge(clsx(inputs));
export const cx = cn;
// Capacity the dashboard stores for "Unlimited" (the date needs a number).
export const UNLIMITED_CAPACITY = 500;
export const FONT = "";
export const INK = "tw-text-foreground";

export const R = {
  sm: "tw-rounded-[var(--r-sm)]",
  md: "tw-rounded-[var(--r-md)]",
  lg: "tw-rounded-[var(--r-lg)]",
  xl: "tw-rounded-[var(--r-xl)]",
  "2xl": "tw-rounded-[var(--r-2xl)]",
  "3xl": "tw-rounded-[var(--r-3xl)]",
  card: "tw-rounded-[var(--r-card)]",
  sheet: "tw-rounded-[var(--r-sheet)]",
};

const FOCUS = "focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring/40";

// ------------------------------------------------------------------ Button
// Luma buttons: pill (16px radius), medium 38px / small 30px, 500 weight.
// "solid light" = translucent ink fill; primary = Sotravel blue.

const buttonVariants = cva(
  cn(
    "tw-inline-flex tw-shrink-0 tw-items-center tw-justify-center tw-whitespace-nowrap tw-border tw-border-transparent tw-font-semibold tw-transition-all tw-select-none active:tw-scale-[0.98] disabled:tw-pointer-events-none disabled:tw-opacity-50 [&_svg]:tw-pointer-events-none [&_svg]:tw-shrink-0 [&_svg:not([class*='size-'])]:tw-size-4",
    R.xl,
    FOCUS
  ),
  {
    variants: {
      variant: {
        default: "tw-bg-primary tw-text-primary-foreground hover:tw-bg-primary/85",
        light: "tw-bg-[var(--fill-strong)] tw-text-foreground hover:tw-bg-[var(--fill-stronger)]",
        outline: "tw-border-border tw-bg-background hover:tw-bg-[var(--fill)]",
        secondary: "tw-bg-[var(--fill)] tw-text-foreground hover:tw-bg-[var(--fill-strong)]",
        ghost: "tw-text-foreground hover:tw-bg-[var(--fill)]",
        destructive: "tw-bg-destructive tw-text-destructive-foreground hover:tw-bg-destructive/90",
        "destructive-light": "tw-bg-destructive/10 tw-text-destructive hover:tw-bg-destructive/15",
        link: "tw-text-foreground tw-underline-offset-4 hover:tw-underline",
        brand: "tw-bg-[hsl(var(--brand))] tw-text-white hover:tw-bg-[hsl(var(--brand))]/85",
        tint: "tw-bg-[hsl(var(--tint))] tw-text-white hover:tw-opacity-90",
      },
      size: {
        default: "tw-h-[38px] tw-gap-1.5 tw-px-3.5 tw-text-[15px]",
        sm: "tw-h-[30px] tw-gap-1 tw-px-2.5 tw-text-sm",
        xs: "tw-h-6 tw-gap-1 tw-px-2 tw-text-xs [&_svg:not([class*='size-'])]:tw-size-3",
        lg: "tw-h-11 tw-gap-2 tw-px-5 tw-text-base",
        icon: "tw-size-[38px]",
        "icon-sm": "tw-size-[30px]",
        "icon-xs": "tw-size-6 [&_svg:not([class*='size-'])]:tw-size-3",
        "icon-lg": "tw-size-11",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  }
);

export const Button = React.forwardRef(({ className, variant, size, asChild = false, loading = false, children, type = "button", ...props }, ref) => {
  const classes = cn(buttonVariants({ variant, size }), className);
  if (asChild) {
    return (
      <Slot ref={ref} className={classes} {...props}>
        {children}
      </Slot>
    );
  }
  return (
    // `disabled` after the spread: a loading button is never clickable, even
    // when the caller passes disabled={false}.
    <button ref={ref} type={type} className={classes} {...props} disabled={props.disabled || loading}>
      {loading && <LoaderCircle className="tw-animate-spin" />}
      {children}
    </button>
  );
});
Button.displayName = "Button";
export { buttonVariants };
export const PrimaryButton = (p) => <Button {...p} />;
export const SecondaryButton = (p) => <Button variant="light" {...p} />;
export const GhostButton = (p) => <Button variant="ghost" {...p} />;
export const DangerButton = (p) => <Button variant="destructive" {...p} />;
export const TextLink = (p) => <Button variant="link" className="tw-h-auto tw-p-0" {...p} />;

// A Luma "chip" — small pill with icon + label + chevron (calendar picker,
// visibility picker, theme picker trigger).
export const Chip = React.forwardRef(({ className, children, chevron = true, active, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      "tw-inline-flex tw-h-[34px] tw-items-center tw-gap-1.5 tw-bg-[var(--fill-strong)] tw-px-2.5 tw-pr-2 tw-text-sm tw-font-medium tw-text-foreground tw-transition-colors hover:tw-bg-[var(--fill-stronger)]",
      R.xl,
      FOCUS,
      active && "tw-ring-2 tw-ring-ring/40",
      className
    )}
    {...props}
  >
    {children}
    {chevron && <ChevronDown className="tw-size-3.5 tw-opacity-60" />}
  </button>
));
Chip.displayName = "Chip";

// -------------------------------------------------------------------- Card
// Luma cards: 12px radius, hairline ring, no shadow. `squircle` = 24px.

export const Card = React.forwardRef(({ className, squircle = false, fill = false, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("tw-overflow-hidden tw-text-sm tw-text-card-foreground", fill ? "cs-fill" : "tw-bg-card cs-hairline", squircle ? R["3xl"] : R.card, className)}
    {...props}
  />
));
Card.displayName = "Card";
export const CardHeader = ({ className, ...props }) => <div className={cn("tw-grid tw-auto-rows-min tw-items-start tw-gap-1 tw-px-4 tw-pt-4", className)} {...props} />;
export const CardTitle = ({ className, as: Tag = "h3", ...props }) => <Tag className={cn("tw-text-base tw-font-semibold tw-leading-snug", className)} {...props} />;
export const CardDescription = ({ className, ...props }) => <p className={cn("tw-text-sm tw-text-muted-foreground", className)} {...props} />;
export const CardContent = ({ className, ...props }) => <div className={cn("tw-p-4", className)} {...props} />;
export const CardFooter = ({ className, ...props }) => <div className={cn("tw-flex tw-items-center tw-px-4 tw-pb-4", className)} {...props} />;

export const SectionCard = ({ title, description, actions, children, className = "", noPadding = false, contentClassName = "", fill = false }) => (
  <Card className={className} fill={fill}>
    {(title || actions) && (
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-px-4 tw-pt-4">
        <div className="tw-min-w-0">
          {title && <CardTitle>{title}</CardTitle>}
          {description && <CardDescription className="tw-mt-0.5">{description}</CardDescription>}
        </div>
        {actions && <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">{actions}</div>}
      </div>
    )}
    <div className={cn(noPadding ? cn(title || actions ? "tw-pt-3" : "") : "tw-p-4", contentClassName)}>{children}</div>
  </Card>
);

// A Luma page section: title row (with optional action) + description + body,
// separated from the next by a hairline.
export const Section = ({ title, description, actions, children, className = "", first = false }) => (
  <section className={cn(first ? "tw-pb-6" : "tw-py-6", className)}>
    {(title || actions) && (
      // Title and action share the first row; on phones the description runs under
      // both at full width instead of being squeezed beside the button.
      <div className="tw-mb-3 tw-grid tw-grid-cols-[minmax(0,1fr)_auto] tw-items-start tw-gap-x-4">
        {title && <h2 className="cs-section-title tw-col-start-1 tw-row-start-1">{title}</h2>}
        {actions && <div className="tw-col-start-2 tw-row-start-1 tw-flex tw-shrink-0 tw-items-center tw-gap-2 sm:tw-row-span-2">{actions}</div>}
        {description && <p className="tw-col-span-2 tw-mt-1 tw-text-[15px] tw-text-muted-foreground sm:tw-col-span-1 sm:tw-col-start-1">{description}</p>}
      </div>
    )}
    {children}
  </section>
);

// ------------------------------------------------------------------ Layout

export const PageHeader = ({ title, description, actions, eyebrow, className = "" }) => (
  <div className={cn("tw-mb-5 tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-4", className)}>
    <div className="tw-min-w-0">
      {eyebrow && <p className="tw-mb-1 tw-text-sm tw-font-medium tw-text-muted-foreground">{eyebrow}</p>}
      <h1 className="cs-title">{title}</h1>
      {description && <p className="tw-mt-1 tw-max-w-2xl tw-text-[15px] tw-text-muted-foreground">{description}</p>}
    </div>
    {actions && <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">{actions}</div>}
  </div>
);
export const PageTitle = ({ children, subtitle, actions }) => <PageHeader title={children} description={subtitle} actions={actions} />;

// Horizontal tab links under a page title (Luma manage pages).
export const PageTabs = ({ tabs, className = "" }) => (
  <nav className={cn("cs-tabbar", className)} aria-label="Sections">
    {tabs.map((tab) => (
      <NavLink key={tab.to} to={tab.to} end={tab.end} className="tw-shrink-0">
        {({ isActive }) => (
          <span className="cs-tab tw-block" data-state={isActive ? "active" : "inactive"}>
            {tab.label}
            {tab.count != null && <span className="tw-ml-1.5 tw-text-muted-foreground">{tab.count}</span>}
            {tab.unread && (
              <span
                className="tw-ml-1.5 tw-inline-block tw-size-2 tw-rounded-full tw-bg-destructive tw-align-middle"
                aria-label="Unread updates"
                title="Unread updates"
              />
            )}
          </span>
        )}
      </NavLink>
    ))}
  </nav>
);

export const Separator = ({ className = "", orientation = "horizontal" }) => (
  <div role="separator" className={cn("tw-shrink-0 tw-bg-[var(--hairline)]", orientation === "vertical" ? "tw-h-full tw-w-px" : "tw-h-px tw-w-full", className)} />
);
export const Divider = () => <Separator className="tw-my-5" />;
export const Skeleton = ({ className = "" }) => <div className={cn("tw-animate-pulse tw-bg-[var(--fill-strong)]", R.md, className)} />;
export const Spinner = ({ className = "" }) => <LoaderCircle className={cn("tw-size-5 tw-animate-spin tw-text-muted-foreground", className)} />;

// ------------------------------------------------------------------- Forms
// Luma inputs: 16px text, 8px radius, translucent fill, ring on focus.

export const inputClass = cn(
  "tw-flex tw-h-11 tw-w-full tw-min-w-0 tw-border tw-border-transparent tw-bg-[var(--fill)] tw-px-3.5 tw-py-2 tw-text-base tw-text-foreground tw-transition-[background,box-shadow] tw-duration-150 placeholder:tw-text-muted-foreground/70 hover:tw-bg-[var(--fill-strong)] focus:tw-bg-background disabled:tw-pointer-events-none disabled:tw-cursor-not-allowed disabled:tw-opacity-50",
  R.md,
  "focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring/40"
);
const invalidClass = "tw-ring-2 tw-ring-destructive/40 focus-visible:tw-ring-destructive/40";

export const Input = React.forwardRef(({ className, invalid, ...props }, ref) => (
  <input ref={ref} aria-invalid={invalid || undefined} className={cn(inputClass, invalid && invalidClass, className)} {...props} />
));
Input.displayName = "Input";
export const TextInput = Input;

export const Textarea = React.forwardRef(({ className, invalid, rows = 4, autoGrow = false, ...props }, ref) => {
  const innerRef = useRef(null);
  const setRef = (el) => {
    innerRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  };
  useEffect(() => {
    if (!autoGrow || !innerRef.current) return;
    const el = innerRef.current;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [props.value, autoGrow]);
  return <textarea ref={setRef} rows={rows} aria-invalid={invalid || undefined} className={cn(inputClass, "tw-h-auto tw-min-h-[88px] tw-py-2.5 tw-leading-relaxed", invalid && invalidClass, className)} {...props} />;
});
Textarea.displayName = "Textarea";
export const TextArea = Textarea;

export const Label = ({ className, ...props }) => <label className={cn("tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-leading-none tw-text-muted-foreground tw-select-none", className)} {...props} />;

export const Field = ({ label, hint, error, children, right, htmlFor, optional, className = "" }) => {
  const { t } = useTranslation();
  return (
    <div className={cn("tw-flex tw-flex-col tw-gap-1.5", className)}>
      {(label || right) && (
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
          {label && (
            <Label htmlFor={htmlFor}>
              {label}
              {optional && <span className="tw-font-normal tw-text-muted-foreground/70">{t("club_optional", { defaultValue: "optional" })}</span>}
            </Label>
          )}
          {right}
        </div>
      )}
      {children}
      {error ? (
        <p className="tw-text-[13px] tw-font-medium tw-text-destructive" role="alert">{error}</p>
      ) : hint ? (
        <p className="tw-text-[13px] tw-text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
};

export const Select = React.forwardRef(({ className, children, size = "default", invalid, ...props }, ref) => (
  <div className={cn("tw-relative", className)}>
    <select ref={ref} aria-invalid={invalid || undefined} className={cn(inputClass, "tw-appearance-none tw-pr-9", size === "sm" && "tw-h-[34px] tw-text-sm", invalid && invalidClass)} {...props}>
      {children}
    </select>
    <ChevronDown className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 tw-h-4 tw-w-4 tw--translate-y-1/2 tw-text-muted-foreground" />
  </div>
));
Select.displayName = "Select";

// Action bar that always floats at the bottom of the screen (trip creator, More tab),
// lined up with the page column. ManageLayout leaves room under the footer for it.
export const FloatingBar = ({ className = "", children }) => (
  <div className="tw-pointer-events-none tw-fixed tw-inset-x-0 tw-bottom-[calc(1rem+var(--app-tabbar-inset,0px))] tw-z-20 tw-px-4 md:tw-px-6">
    <div
      className={cn(
        "tw-pointer-events-auto tw-mx-auto tw-flex tw-max-w-[772px] tw-items-center tw-gap-2 tw-rounded-[var(--r-2xl)] tw-bg-background/90 tw-p-2 tw-shadow-lg tw-ring-1 tw-ring-black/5 tw-backdrop-blur",
        className
      )}
    >
      {children}
    </div>
  </div>
);

// Luma toggle: 44×26 pill, green when on.
export const Switch = ({ checked, onCheckedChange, disabled, id, className = "", size = "default", ...props }) => (
  <button
    id={id}
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onCheckedChange?.(!checked)}
    className={cn(
      "tw-relative tw-inline-flex tw-shrink-0 tw-items-center tw-rounded-full tw-transition-colors disabled:tw-cursor-not-allowed disabled:tw-opacity-50",
      FOCUS,
      size === "sm" ? "tw-h-5 tw-w-8" : "tw-h-[26px] tw-w-11",
      checked ? "tw-bg-[#3cbd2c]" : "tw-bg-[var(--fill-stronger)]",
      className
    )}
    {...props}
  >
    <span className={cn("tw-pointer-events-none tw-block tw-rounded-full tw-bg-white tw-shadow tw-transition-transform", size === "sm" ? "tw-size-4 tw-translate-x-0.5" : "tw-size-[22px] tw-translate-x-0.5", checked && (size === "sm" ? "tw-translate-x-[14px]" : "tw-translate-x-[20px]"))} />
  </button>
);

export const Toggle = ({ checked, onChange, label, description, className = "" }) => {
  const id = useId();
  return (
    <div className={cn("tw-flex tw-items-center tw-justify-between tw-gap-4 tw-bg-[var(--fill)] tw-p-4", R.card, className)}>
      <div className="tw-space-y-0.5">
        <label htmlFor={id} className="tw-cursor-pointer tw-text-[15px] tw-font-medium">{label}</label>
        {description && <p className="tw-text-[13px] tw-text-muted-foreground">{description}</p>}
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  );
};

// Segmented control (Luma "Upcoming | Past").
export const Segmented = ({ value, onChange, options, className = "", size = "default" }) => (
  <div className={cn("tw-inline-flex tw-items-center tw-gap-0.5 tw-bg-[var(--fill)] tw-p-1", R.xl, className)} role="tablist">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        role="tab"
        aria-selected={value === o.value}
        onClick={() => onChange(o.value)}
        className={cn(
          "tw-inline-flex tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-font-medium tw-transition-colors",
          R.lg,
          FOCUS,
          size === "sm" ? "tw-h-7 tw-px-2.5 tw-text-[13px]" : "tw-h-8 tw-px-3 tw-text-sm",
          value === o.value ? "tw-bg-[var(--fill-stronger)] tw-text-foreground" : "tw-text-muted-foreground hover:tw-text-foreground"
        )}
      >
        {o.icon && <o.icon className="tw-size-3.5" />}
        {o.label}
        {o.count != null && <span className="tw-text-muted-foreground">{o.count}</span>}
      </button>
    ))}
  </div>
);
export const ChipGroup = ({ value, onChange, options, className = "" }) => (
  <div className={cn("tw-flex tw-flex-wrap tw-gap-1.5", className)} role="radiogroup">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        role="radio"
        aria-checked={value === o.value}
        onClick={() => onChange(o.value)}
        className={cn("tw-h-8 tw-px-3 tw-text-sm tw-font-medium tw-transition-colors", R.xl, FOCUS, value === o.value ? "tw-bg-primary tw-text-primary-foreground" : "tw-bg-[var(--fill)] tw-text-foreground hover:tw-bg-[var(--fill-strong)]")}
      >
        {o.label}
      </button>
    ))}
  </div>
);

// Luma "event options" list: rows with icon · label · value/control.
export const OptionList = ({ children, className = "" }) => <div className={cn("tw-overflow-hidden tw-bg-[var(--fill)]", R.card, className)}>{children}</div>;
export const OptionRow = ({ icon: Icon, label, hint, value, onClick, children, className = "", as, wrapHint = false }) => {
  const Tag = as || (onClick ? "button" : "div");
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} className={cn("cs-row tw-w-full tw-text-left tw-text-[15px]", onClick && "hover:tw-bg-[var(--fill)] tw-transition-colors", className)}>
      {Icon && <Icon className="tw-size-[18px] tw-shrink-0 tw-text-muted-foreground" />}
      <span className="tw-min-w-0 tw-flex-1 tw-py-2.5">
        <span className="tw-block tw-truncate tw-font-medium">{label}</span>
        {hint && <span className={cn("tw-block tw-text-[13px] tw-text-muted-foreground", wrapHint ? "tw-line-clamp-2" : "tw-truncate")}>{hint}</span>}
      </span>
      {value != null && <span className="tw-shrink-0 tw-text-muted-foreground">{value}</span>}
      {children}
    </Tag>
  );
};

// Currency amount input with a code prefix.
export const MoneyInput = ({ currency, value, onChange, placeholder = "0", className = "", inputClassName = "", invalid, disabled, id }) => (
  <div className={cn("tw-relative", className)}>
    <span className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw--translate-y-1/2 tw-text-sm tw-font-medium tw-text-muted-foreground">{String(currency || "").toUpperCase()}</span>
    <Input
      id={id}
      type="number"
      min="0"
      step="1"
      inputMode="numeric"
      pattern="[0-9]*"
      value={value}
      onKeyDown={(e) => [".", ",", "e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
      onChange={(e) => /^\d*$/.test(e.target.value) && onChange(e.target.value)}
      placeholder={placeholder}
      invalid={invalid}
      disabled={disabled}
      className={cn("tw-pl-14 cs-num", inputClassName)}
    />
  </div>
);

// ----------------------------------------------------------------- Popover
// Anchored floating panel (portal). Closes on outside click / Escape and
// follows its anchor when any scroll container moves.

// placement "auto" flips above the anchor when there's no room below;
// "bottom" always opens below (search suggestions belong under the search
// box) and caps its height to the visible viewport — on phones that's the
// space above the keyboard (visualViewport).
export const Popover = ({ open, onClose, anchorRef, children, align = "start", width, className = "", offset = 6, matchWidth = false, placement = "auto" }) => {
  const [pos, setPos] = useState(null);
  const panelRef = useRef(null);
  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) return undefined;
    const place = () => {
      const r = anchorRef.current.getBoundingClientRect();
      const w = matchWidth ? r.width : width || Math.max(r.width, 220);
      const vv = window.visualViewport;
      // In the app the floating tab bar covers the bottom of the page unless
      // the keyboard is up (the bar hides then).
      const keyboardUp = vv ? window.innerHeight - vv.height > 120 : false;
      const barInset = keyboardUp ? 0 : parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--app-tabbar-inset")) || 0;
      const viewportBottom = (vv ? vv.offsetTop + vv.height : window.innerHeight) - barInset;
      const spaceBelow = viewportBottom - r.bottom;
      const flip = placement !== "bottom" && spaceBelow < 320 && r.top > spaceBelow;
      const left = align === "end" ? Math.max(8, r.right - w) : Math.min(r.left, window.innerWidth - w - 8);
      setPos({
        left: Math.max(8, left),
        top: flip ? undefined : r.bottom + offset,
        bottom: flip ? window.innerHeight - r.top + offset : undefined,
        width: w,
        maxHeight: placement === "bottom" ? Math.max(140, spaceBelow - offset - 8) : undefined,
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    window.visualViewport?.addEventListener("resize", place);
    window.visualViewport?.addEventListener("scroll", place);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      window.visualViewport?.removeEventListener("resize", place);
      window.visualViewport?.removeEventListener("scroll", place);
    };
  }, [open, anchorRef, align, width, offset, matchWidth, placement]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || anchorRef?.current?.contains(e.target)) return;
      onClose?.();
    };
    // Capture phase + preventDefault: Escape closes this panel, not the dialog under it.
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      onClose?.();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open, onClose, anchorRef]);
  if (!open || !pos) return null;
  return createPortal(
    <div className="club-admin tw-contents">
      <motion.div
        ref={panelRef}
        initial={{ opacity: 0, y: -4, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.12 }}
        style={{ position: "fixed", left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.width, maxHeight: pos.maxHeight, overflowY: pos.maxHeight ? "auto" : undefined, zIndex: 4500 }}
        className={cn("cs-popover-shadow tw-bg-popover tw-p-1.5 tw-text-popover-foreground", R.lg, className)}
      >
        {children}
      </motion.div>
    </div>,
    document.body
  );
};

// Menu item inside a Popover / DropdownMenu.
export const MenuItem = ({ icon: Icon, label, hint, onClick, danger, disabled, right, selected, className = "" }) => (
  <button
    type="button"
    role="menuitem"
    disabled={disabled}
    onClick={onClick}
    className={cn(
      "tw-flex tw-w-full tw-items-center tw-gap-2.5 tw-px-2.5 tw-py-2 tw-text-left tw-text-[15px] tw-transition-colors disabled:tw-opacity-50",
      R.md,
      danger ? "tw-text-destructive hover:tw-bg-destructive/10" : "hover:tw-bg-[var(--fill)]",
      className
    )}
  >
    {Icon && <Icon className={cn("tw-size-4 tw-shrink-0", danger ? "" : "tw-text-muted-foreground")} />}
    <span className="tw-min-w-0 tw-flex-1">
      <span className="tw-block tw-truncate tw-font-medium">{label}</span>
      {hint && <span className="tw-block tw-text-[13px] tw-text-muted-foreground">{hint}</span>}
    </span>
    {right}
    {selected && <CheckCircle2 className="tw-size-4 tw-shrink-0" />}
  </button>
);

// items: [{ label, icon, onClick, danger, disabled, separator, hint, selected }]
export const DropdownMenu = ({ items, trigger, ariaLabel, align = "end", width = 220, block = false, menuClassName = "" }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const visible = (items || []).filter(Boolean);
  return (
    <>
      <span ref={btnRef} className={cn("tw-inline-flex tw-max-w-full", block && "tw-flex tw-w-full")}>
        {trigger ? (
          React.cloneElement(trigger, { onClick: () => setOpen((v) => !v), "aria-haspopup": "menu", "aria-expanded": open, "aria-label": trigger.props["aria-label"] || ariaLabel })
        ) : (
          <Button variant="ghost" size="icon-sm" aria-label={ariaLabel} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="tw-text-muted-foreground hover:tw-text-foreground">
            <MoreHorizontal />
          </Button>
        )}
      </span>
      <Popover open={open} onClose={() => setOpen(false)} anchorRef={btnRef} align={align} width={width}>
        <div role="menu" className={menuClassName}>
          {visible.map((item, i) =>
            item.separator ? (
              <div key={`sep-${i}`} className="tw--mx-1.5 tw-my-1 tw-h-px tw-bg-[var(--hairline)]" />
            ) : item.heading ? (
              <p key={`h-${i}`} className="tw-px-2.5 tw-pb-1 tw-pt-2 tw-text-[13px] tw-text-muted-foreground">{item.heading}</p>
            ) : (
              (({ key: itemKey, ...rest }) => (
                <MenuItem
                  key={itemKey || rest.label}
                  {...rest}
                  onClick={() => {
                    setOpen(false);
                    rest.onClick?.();
                  }}
                />
              ))(item)
            )
          )}
        </div>
      </Popover>
    </>
  );
};
export const RowMenu = DropdownMenu;

// ------------------------------------------------------------------- Badge

const badgeVariants = cva(
  cn("tw-inline-flex tw-h-6 tw-w-fit tw-shrink-0 tw-items-center tw-justify-center tw-gap-1 tw-whitespace-nowrap tw-px-2 tw-text-xs tw-font-medium [&>svg]:tw-size-3", R.md),
  {
    variants: {
      variant: {
        default: "tw-bg-primary tw-text-primary-foreground",
        secondary: "tw-bg-[var(--fill-strong)] tw-text-foreground",
        outline: "tw-ring-1 tw-ring-[var(--hairline)] tw-text-foreground",
        success: "cs-badge-success tw-bg-[#3cbd2c]/12 tw-text-[#2d8e21]",
        warning: "cs-badge-warning tw-bg-[#eaab26]/15 tw-text-[#926b18]",
        info: "cs-badge-info tw-bg-blue-50 tw-text-[#125dce]",
        destructive: "tw-bg-destructive/10 tw-text-destructive",
        muted: "tw-bg-[var(--fill)] tw-text-muted-foreground",
        tint: "cs-badge-tint tw-bg-[hsl(var(--tint-soft))] tw-text-[hsl(var(--tint-text))]",
      },
    },
    defaultVariants: { variant: "secondary" },
  }
);
export const Badge = ({ className, variant, dot, children, ...props }) => (
  <span className={cn(badgeVariants({ variant }), className)} {...props}>
    {dot && <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-current tw-opacity-80" />}
    {children}
  </span>
);

export const StatusBadge = ({ status, className = "" }) => {
  const { t } = useTranslation();
  const map = {
    live: ["success", t("club_status_live", { defaultValue: "Public" })],
    public: ["success", t("club_status_live", { defaultValue: "Public" })],
    unlisted: ["warning", t("club_status_unlisted", { defaultValue: "Private" })],
    private: ["warning", t("club_status_unlisted", { defaultValue: "Private" })],
    waitlist: ["info", t("club_vis_waitlist", { defaultValue: "Waitlist" })],
    draft: ["muted", t("club_status_draft", { defaultValue: "No dates yet" })],
    ended: ["muted", t("club_status_ended", { defaultValue: "Ended" })],
    cancelled: ["destructive", t("club_status_cancelled", { defaultValue: "Cancelled" })],
    pending: ["warning", t("club_sale_pending", { defaultValue: "Pending" })],
    available: ["info", t("club_sale_available", { defaultValue: "Ready to pay out" })],
    processing: ["info", t("club_payout_processing", { defaultValue: "Processing" })],
    completed: ["success", t("club_payout_completed", { defaultValue: "Paid" })],
    paid: ["success", t("club_payout_completed", { defaultValue: "Paid" })],
    rejected: ["destructive", t("club_payout_rejected", { defaultValue: "Rejected" })],
    reserved: ["success", t("club_guest_reserved", { defaultValue: "Reserved" })],
    waitlisted: ["info", t("club_guest_waitlisted", { defaultValue: "Waitlisted" })],
    going: ["success", t("club_guest_going", { defaultValue: "Going" })],
    checked_in: ["info", t("club_guest_checked_in", { defaultValue: "Checked in" })],
    viewed: ["info", t("club_sale_viewed", { defaultValue: "Browsing" })],
  };
  const [variant, label] = map[status] || map.draft;
  return <Badge variant={variant} className={className}>{label}</Badge>;
};

// ------------------------------------------------------------------ Avatar

export const Avatar = ({ src, name, size = 32, className = "", square = false, brand = false }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  const initials = (name || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "?";
  const generated = /ui-avatars\.com/.test(src || "");
  const showImage = Boolean(src) && !broken && !generated;
  // Clubs without a photo get a Sotravel-blue tile behind their initials.
  const bg = brand ? { background: "linear-gradient(135deg, #8fe3ff, #00bfff)" } : undefined;
  return (
    <span
      className={cn("tw-relative tw-inline-flex tw-shrink-0 tw-select-none tw-items-center tw-justify-center tw-overflow-hidden tw-bg-[var(--fill-strong)] tw-font-semibold tw-text-foreground/70", square ? R.lg : "tw-rounded-full", className)}
      style={{ width: size, height: size, fontSize: Math.max(11, Math.round(size * 0.38)), ...bg }}
      aria-hidden="true"
    >
      {showImage ? <img src={src} alt="" onError={() => setBroken(true)} className="tw-h-full tw-w-full tw-object-cover" /> : <span className={brand ? "tw-text-white" : undefined}>{initials}</span>}
    </span>
  );
};

// ------------------------------------------------------------------- Alert

export const Alert = ({ variant = "default", title, children, className = "", icon, action }) => {
  const styles = {
    default: ["tw-text-foreground", Info],
    info: ["tw-text-[#125dce]", Info],
    warning: ["tw-text-[#b98a27]", AlertTriangle],
    destructive: ["tw-text-destructive", AlertCircle],
    success: ["tw-text-[#2d8e21]", CheckCircle2],
  };
  const [tone, DefaultIcon] = styles[variant] || styles.default;
  const Icon = icon === null ? null : icon || DefaultIcon;
  return (
    // With an action button (and no title) the row is centred so the icon,
    // text and button line up; a titled alert keeps top alignment.
    <div role="alert" className={cn("tw-flex tw-w-full tw-gap-3 tw-bg-[var(--fill)] tw-px-4 tw-py-3 tw-text-left tw-text-sm", action && !title ? "tw-items-center" : "tw-items-start", R.card, className)}>
      {Icon && <Icon className={cn("tw-size-4 tw-shrink-0", tone, !(action && !title) && "tw-mt-0.5")} />}
      <div className="tw-min-w-0 tw-flex-1">
        {title && <p className={cn("tw-font-medium", tone)}>{title}</p>}
        <div className={cn("tw-text-sm", title ? "tw-text-muted-foreground" : "tw-text-foreground", "[&_p]:tw-leading-relaxed")}>{children}</div>
      </div>
      {action}
    </div>
  );
};
export const InlineAlert = ({ tone = "info", children }) => <Alert variant={tone === "error" ? "destructive" : tone}>{children}</Alert>;

// ------------------------------------------------------------------- Table

export const Table = ({ className, ...props }) => (
  <div className="tw-relative tw-w-full tw-overflow-x-auto">
    <table className={cn("tw-w-full tw-caption-bottom tw-border-collapse tw-text-sm", className)} {...props} />
  </div>
);
export const TableHeader = ({ className, ...props }) => <thead className={cn("[&_tr]:tw-border-b [&_tr]:tw-border-[var(--hairline)]", className)} {...props} />;
export const TableBody = ({ className, ...props }) => <tbody className={cn("[&_tr:last-child]:tw-border-0", className)} {...props} />;
export const TableRow = ({ className, ...props }) => <tr className={cn("tw-border-b tw-border-[var(--hairline)] tw-transition-colors hover:tw-bg-[var(--fill)]", className)} {...props} />;
export const TableHead = ({ className, ...props }) => <th className={cn("tw-h-10 tw-whitespace-nowrap tw-px-3 tw-text-left tw-align-middle tw-text-[13px] tw-font-medium tw-text-muted-foreground first:tw-pl-4 last:tw-pr-4", className)} {...props} />;
export const TableCell = ({ className, ...props }) => <td className={cn("tw-whitespace-nowrap tw-px-3 tw-py-3 tw-align-middle first:tw-pl-4 last:tw-pr-4", className)} {...props} />;

// -------------------------------------------------------------------- Tabs
// Pill tab list (Luma: Upcoming / Past). PageTabs above is the underline kind.

const TabsCtx = createContext(null);
export const Tabs = ({ value, onValueChange, children, className = "" }) => (
  <TabsCtx.Provider value={{ value, onValueChange }}>
    <div className={className}>{children}</div>
  </TabsCtx.Provider>
);
export const TabsList = ({ className, ...props }) => <div role="tablist" className={cn("tw-inline-flex tw-h-9 tw-w-fit tw-items-center tw-justify-center tw-gap-0.5 tw-bg-[var(--fill)] tw-p-1 tw-text-muted-foreground", R.xl, className)} {...props} />;
export const TabsTrigger = ({ value, className, children, ...props }) => {
  const ctx = useContext(TabsCtx);
  const active = ctx?.value === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={() => ctx?.onValueChange?.(value)}
      className={cn("tw-inline-flex tw-h-full tw-items-center tw-justify-center tw-gap-1.5 tw-whitespace-nowrap tw-px-3 tw-text-sm tw-font-medium tw-transition-all", R.lg, FOCUS, active ? "tw-bg-[var(--fill-stronger)] tw-text-foreground" : "tw-text-muted-foreground hover:tw-text-foreground", className)}
      {...props}
    >
      {children}
    </button>
  );
};

// ------------------------------------------------------------------ Dialog

// Lock the page behind a dialog. It's <html> that scrolls on this site and
// <body> is pinned to the viewport height, so hiding body overflow would clip
// the page and jump it back to the top; hiding it on <html> keeps the spot.
const useLockScroll = (locked) => {
  useLayoutEffect(() => {
    if (!locked) return undefined;
    const root = document.documentElement;
    const prev = root.style.overflow;
    const prevGutter = root.style.scrollbarGutter;
    root.style.overflow = "hidden";
    // Keep the scrollbar's room on desktop so the page doesn't shift sideways.
    root.style.scrollbarGutter = "stable";
    // Open dialogs, counted: floating cards meant for the page underneath
    // (e.g. the profile's "Switch to leading" pill) step aside — see index.css.
    root.dataset.kitDialogs = String(Number(root.dataset.kitDialogs || 0) + 1);
    return () => {
      root.style.overflow = prev;
      root.style.scrollbarGutter = prevGutter;
      const left = Number(root.dataset.kitDialogs || 1) - 1;
      if (left > 0) root.dataset.kitDialogs = String(left);
      else delete root.dataset.kitDialogs;
    };
  }, [locked]);
};

// Phones get a bottom sheet (full width, flush to the bottom, drag the handle down
// to close); wider screens a centred dialog. Matches Tailwind's `sm` breakpoint.
const SHEET_QUERY = "(max-width: 639px)";
export const useBottomSheet = () => {
  const [sheet, setSheet] = useState(() => typeof window !== "undefined" && window.matchMedia?.(SHEET_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(SHEET_QUERY);
    if (!mq) return undefined;
    const update = () => setSheet(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return sheet;
};

// Open dialogs, newest last: Escape only closes the one on top when they stack
// (e.g. "Confirm this event?" over More options).
const openDialogs = [];

// `onBack` shows a back arrow before the title, for a step inside the same
// dialog (e.g. More options → Cancellation policy); Done there returns to it.
export const Dialog = ({ open, onClose, onBack, title, description, children, footer, size = "sm", hideClose = false, scope = "club-admin", className = "", closeOnBackdrop = true }) => {
  const { t } = useTranslation();
  const sheet = useBottomSheet();
  const drag = useDragControls();
  const dialogId = useId();
  useLockScroll(open);
  // Handlers in a ref so a re-render (new inline callbacks) doesn't re-register the
  // dialog and move it to the top of the stack.
  const handlers = useRef({ onClose, onBack });
  handlers.current = { onClose, onBack };
  useEffect(() => {
    if (!open) return undefined;
    openDialogs.push(dialogId);
    // Escape acts on the top dialog only; on a step (onBack) it goes back a level.
    const onKey = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented || openDialogs[openDialogs.length - 1] !== dialogId) return;
      const { onBack: back, onClose: close } = handlers.current;
      (back || close)?.();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const at = openDialogs.lastIndexOf(dialogId);
      if (at >= 0) openDialogs.splice(at, 1);
    };
  }, [open, dialogId]);
  const widths = { sm: "sm:tw-max-w-md", md: "sm:tw-max-w-lg", lg: "sm:tw-max-w-2xl", xl: "sm:tw-max-w-4xl" };
  // Start a drag from the handle/header only, so the body keeps scrolling.
  const startDrag = sheet && onClose ? (e) => drag.start(e) : undefined;
  return createPortal(
    <AnimatePresence>
      {open && (
        <div className={cn(scope, "tw-fixed tw-inset-0 tw-z-[4000] tw-flex tw-items-end tw-justify-center sm:tw-items-center sm:tw-p-6")} style={{ background: "transparent" }}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: sheet ? 0.2 : 0.12 }} className="tw-absolute tw-inset-0 tw-bg-[rgba(0,22,36,0.45)] tw-backdrop-blur-[2px]" onClick={closeOnBackdrop ? onClose : undefined} />
          <motion.div
            role="dialog"
            aria-modal="true"
            data-bottom-sheet={sheet ? "true" : undefined}
            initial={sheet ? { y: "100%" } : { opacity: 0, scale: 0.97, y: 8 }}
            animate={sheet ? { y: 0 } : { opacity: 1, scale: 1, y: 0 }}
            exit={sheet ? { y: "100%" } : { opacity: 0, scale: 0.97, y: 8 }}
            transition={sheet ? { type: "spring", stiffness: 420, damping: 40 } : { duration: 0.14 }}
            drag={sheet && onClose ? "y" : false}
            dragControls={drag}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.8 }}
            onDragEnd={(e, info) => {
              if (info.offset.y > 120 || info.velocity.y > 700) onClose?.();
            }}
            className={cn(
              "tw-relative tw-z-10 tw-flex tw-w-full tw-flex-col tw-overflow-hidden tw-bg-popover tw-text-sm tw-text-popover-foreground tw-shadow-2xl",
              "tw-max-h-[calc(100dvh-2.5rem)] tw-rounded-t-[var(--r-sheet)] sm:tw-max-h-[calc(100vh-3rem)] sm:tw-rounded-[var(--r-sheet)]",
              widths[size] || widths.sm,
              className
            )}
            // Room for the home indicator (and the app's tab bar if it ever stays up).
            style={sheet ? { paddingBottom: "calc(max(env(safe-area-inset-bottom), var(--app-safe-bottom, 0px)) + var(--app-tabbar-inset, 0px))" } : undefined}
          >
            {sheet && (
              <div onPointerDown={startDrag} className="tw-flex tw-shrink-0 tw-touch-none tw-justify-center tw-pb-0.5 tw-pt-2" aria-hidden="true">
                <span className="tw-h-1.5 tw-w-10 tw-rounded-full tw-bg-[var(--fill-stronger)]" />
              </div>
            )}
            {(title || !hideClose || onBack) && (
              <div onPointerDown={startDrag} className={cn("tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-[var(--hairline)] tw-px-5 tw-py-3.5", sheet && "tw-touch-none tw-pt-2")}>
                <div className="tw-flex tw-min-w-0 tw-items-start tw-gap-2">
                  {onBack && (
                    <button type="button" onClick={onBack} onPointerDown={(e) => e.stopPropagation()} aria-label={t("back", { defaultValue: "Back" })} className={cn("tw--ml-1.5 tw-flex tw-size-8 tw-shrink-0 tw-items-center tw-justify-center tw-text-foreground tw-transition-colors hover:tw-bg-[var(--fill)]", "tw-rounded-full")}>
                      <ChevronLeft className="tw-size-5" />
                    </button>
                  )}
                  <div className={cn("tw-min-w-0", onBack && "tw-pt-1")}>
                    {title && <h2 className="tw-text-base tw-font-semibold tw-leading-tight">{title}</h2>}
                    {description && <p className="tw-mt-1.5 tw-text-sm tw-text-muted-foreground">{description}</p>}
                  </div>
                </div>
                {!hideClose && (
                  <button type="button" onClick={onClose} onPointerDown={(e) => e.stopPropagation()} aria-label={t("close", { defaultValue: "Close" })} className={cn("tw-flex tw-size-8 tw-shrink-0 tw-items-center tw-justify-center tw-self-start tw-bg-[var(--fill)] tw-text-muted-foreground tw-transition-colors hover:tw-bg-[var(--fill-strong)] hover:tw-text-foreground", "tw-rounded-full")}>
                    <X className="tw-size-4" />
                  </button>
                )}
              </div>
            )}
            {children != null && children !== false && <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-overscroll-contain tw-px-5 tw-py-4">{children}</div>}
            {footer && <div className={cn("tw-flex tw-shrink-0 tw-flex-col-reverse tw-gap-2 tw-px-5 tw-py-3.5 sm:tw-flex-row sm:tw-justify-end", children != null && children !== false && "tw-border-t tw-border-[var(--hairline)]")}>{footer}</div>}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export const ConfirmModal = ({ open, onClose, onConfirm, onCancel, title, message, confirmLabel, cancelLabel, danger = false, loading = false, children, scope }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      title={title}
      description={message}
      scope={scope}
      footer={
        <>
          <Button variant="light" onClick={onCancel || onClose} disabled={loading}>{cancelLabel || t("cancel", { defaultValue: "Cancel" })}</Button>
          <Button variant={danger ? "destructive" : "default"} onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
        </>
      }
    >
      {children}
    </Dialog>
  );
};

// Right-hand sheet (Luma "Edit Event" drawer). Full-screen on phones.
export const Sheet = ({ open, onClose, title, children, footer, width = 520, scope = "club-admin" }) => {
  const { t } = useTranslation();
  useLockScroll(open);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  return createPortal(
    <AnimatePresence>
      {open && (
        <div className={cn(scope, "tw-fixed tw-inset-0 tw-z-[4000]")} style={{ background: "transparent" }}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.12 }} className="tw-absolute tw-inset-0 tw-bg-[rgba(0,22,36,0.45)]" onClick={onClose} />
          <motion.aside
            role="dialog"
            aria-modal="true"
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 420, damping: 38 }}
            className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-w-full tw-flex-col tw-bg-background tw-pb-[var(--app-tabbar-inset,0px)] tw-shadow-2xl sm:tw-my-2 sm:tw-pb-0 sm:tw-mr-2 sm:tw-max-w-[var(--sheet-w)] sm:tw-rounded-[var(--r-sheet)]"
            style={{ "--sheet-w": `${width}px` }}
          >
            <div className="tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-[var(--hairline)] tw-px-4 tw-py-3">
              <button type="button" onClick={onClose} aria-label={t("close", { defaultValue: "Close" })} className="tw-flex tw-size-8 tw-items-center tw-justify-center tw-rounded-full tw-text-muted-foreground hover:tw-bg-[var(--fill)] hover:tw-text-foreground">
                <ChevronRight className="tw-size-4" />
                <ChevronRight className="tw--ml-3 tw-size-4" />
              </button>
              <h2 className="tw-text-base tw-font-semibold">{title}</h2>
            </div>
            <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-5 tw-py-4">{children}</div>
            {footer && <div className="tw-flex tw-items-center tw-gap-2 tw-border-t tw-border-[var(--hairline)] tw-px-5 tw-py-3">{footer}</div>}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

// ------------------------------------------------------------- Empty state

export const EmptyState = ({ icon: Icon, image, title, description, action, className = "", compact = false }) => (
  <div className={cn("tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-6 tw-text-center", compact ? "tw-py-10" : "tw-py-16", className)}>
    {image ? (
      <div className={cn("tw-mb-4 tw-h-24 tw-w-24 tw-overflow-hidden", R["3xl"])}>
        <img src={image} alt="" className="tw-h-full tw-w-full tw-object-cover" />
      </div>
    ) : Icon ? (
      <div className={cn("tw-mb-4 tw-flex tw-size-16 tw-items-center tw-justify-center tw-bg-[var(--fill)] tw-text-muted-foreground/60", R["3xl"])}><Icon className="tw-size-7" strokeWidth={1.5} /></div>
    ) : null}
    <h3 className="tw-text-lg tw-font-semibold tw-text-muted-foreground">{title}</h3>
    {description && <p className="tw-mt-1 tw-max-w-md tw-text-[15px] tw-text-muted-foreground/80">{description}</p>}
    {action && <div className="tw-mt-5">{action}</div>}
  </div>
);

// --------------------------------------------------------------- Stat tile
// Stat card: icon + label on top, big number, optional hint underneath.

export const StatCard = ({ label, value, hint, icon: Icon, className = "", valueClassName = "", emphasis = false }) => (
  <div className={cn("tw-min-w-0 tw-p-4", R.card, emphasis ? "tw-bg-primary tw-text-primary-foreground" : "cs-fill", className)}>
    <p className={cn("tw-flex tw-items-center tw-gap-1.5 tw-text-[13px] tw-font-medium", emphasis ? "tw-text-primary-foreground/70" : "tw-text-muted-foreground")}>
      {Icon && <Icon className="tw-size-3.5" />}
      {label}
    </p>
    <p className={cn("cs-num tw-mt-1.5 tw-truncate tw-text-2xl tw-font-semibold tw-tracking-tight", valueClassName)}>{value}</p>
    {hint && <p className={cn("tw-mt-0.5 tw-truncate tw-text-[13px]", emphasis ? "tw-text-primary-foreground/70" : "tw-text-muted-foreground")}>{hint}</p>}
  </div>
);

// Big action tile (event Overview quick actions: Share / Check in / View page).
export const ActionTile = ({ icon: Icon, label, hint, onClick, color = "var(--fill-strong)", iconColor, className = "" }) => (
  <button type="button" onClick={onClick} className={cn("tw-flex tw-w-full tw-items-center tw-gap-3 tw-bg-[var(--fill)] tw-p-2.5 tw-text-left tw-transition-colors hover:tw-bg-[var(--fill-strong)]", R.card, className)}>
    <span
      className={cn("tw-flex tw-size-9 tw-shrink-0 tw-items-center tw-justify-center", iconColor && "cs-soft-tile", R.md)}
      style={iconColor ? { "--tile-bg": color, "--tile-fg": iconColor } : { background: color, color: "hsl(var(--foreground))" }}
    >
      <Icon className="tw-size-[18px]" />
    </span>
    <span className="tw-min-w-0">
      <span className="tw-block tw-truncate tw-text-[15px] tw-font-medium">{label}</span>
      {hint && <span className="tw-block tw-truncate tw-text-[13px] tw-text-muted-foreground">{hint}</span>}
    </span>
  </button>
);

// ----------------------------------------------------------- Date & time
// DateField: Luma's small calendar popover (react-day-picker v9).

const DAYPICKER_CLASSES = {
  root: "cs-daypicker tw-p-2 tw-text-sm",
  months: "tw-flex",
  month: "tw-w-[252px]",
  month_caption: "tw-flex tw-h-8 tw-items-center tw-px-1",
  caption_label: "tw-text-base tw-font-semibold",
  nav: "tw-absolute tw-right-2 tw-top-2 tw-flex tw-gap-1",
  button_previous: cn("tw-flex tw-size-7 tw-items-center tw-justify-center tw-rounded-full tw-text-muted-foreground hover:tw-bg-[var(--fill)] disabled:tw-opacity-30"),
  button_next: cn("tw-flex tw-size-7 tw-items-center tw-justify-center tw-rounded-full tw-text-muted-foreground hover:tw-bg-[var(--fill)] disabled:tw-opacity-30"),
  month_grid: "tw-mt-2 tw-w-full tw-border-collapse",
  weekdays: "",
  weekday: "tw-h-8 tw-w-9 tw-text-center tw-text-[13px] tw-font-medium tw-text-muted-foreground",
  week: "",
  day: "tw-p-0 tw-text-center",
  day_button: "tw-flex tw-size-9 tw-items-center tw-justify-center tw-rounded-full tw-text-[15px] tw-font-medium hover:tw-bg-[var(--fill-strong)] disabled:tw-text-muted-foreground/40 disabled:hover:tw-bg-transparent",
  selected: "[&>button]:tw-bg-[#00bfff] [&>button]:tw-text-white [&>button:hover]:tw-bg-[#00bfff]",
  today: "[&>button]:tw-ring-1 [&>button]:tw-ring-[var(--hairline)]",
  // Outside-month dates can still be selected, so they should not look
  // disabled. The day button's disabled variant handles genuinely
  // unavailable dates (for example, dates before `min`).
  outside: "tw-text-foreground",
  disabled: "",
  hidden: "tw-invisible",
};

export const DateField = ({ value, onChange, min, max, className = "", invalid, placeholder = "Pick a date", format = "ddd, D MMM" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = value ? dayjs(value).toDate() : undefined;
  const minDate = min ? dayjs(min).startOf("day").toDate() : undefined;
  const maxDate = max ? dayjs(max).startOf("day").toDate() : undefined;
  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn("tw-flex tw-h-11 tw-items-center tw-truncate tw-bg-[var(--fill)] tw-px-3.5 tw-text-left tw-text-base tw-font-medium hover:tw-bg-[var(--fill-strong)]", R.md, FOCUS, invalid && invalidClass, !value && "tw-text-muted-foreground/70", className)}
      >
        {value ? (format === "ddd, D MMM" ? clubDate(value, "short") : dayjs(value).format(format)) : placeholder}
      </button>
      <Popover open={open} onClose={() => setOpen(false)} anchorRef={ref} width={268}>
        <DayPicker
          mode="single"
          selected={selected}
          defaultMonth={selected}
          disabled={[...(minDate ? [{ before: minDate }] : []), ...(maxDate ? [{ after: maxDate }] : [])]}
          onSelect={(d) => {
            if (d) onChange(dayjs(d).format("YYYY-MM-DD"));
            setOpen(false);
          }}
          classNames={DAYPICKER_CLASSES}
          components={{
            Chevron: ({ orientation }) => (orientation === "left" ? <ChevronLeft className="tw-size-4" /> : <ChevronRight className="tw-size-4" />),
          }}
          showOutsideDays
          weekStartsOn={0}
        />
      </Popover>
    </>
  );
};

// TimeField: native time input styled as a Luma value cell.
export const TimeField = ({ value, onChange, className = "", invalid, step = 300 }) => (
  <input
    type="time"
    lang="en-US"
    step={step}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    aria-invalid={invalid || undefined}
    className={cn("cs-num tw-h-11 tw-w-[132px] tw-shrink-0 tw-bg-[var(--fill)] tw-px-3 tw-text-center tw-text-base tw-font-medium hover:tw-bg-[var(--fill-strong)]", R.md, FOCUS, invalid && invalidClass, className)}
  />
);

// ------------------------------------------------------------ Photo upload

export const PhotoUploader = ({ value, onChange, label, aspect = "tw-aspect-square", round = false, className = "", size = "xl", compact = false, onPick, children }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      onChange(await uploadBigFileToS3(file));
    } catch (e) {
      setError(e.message || t("club_upload_failed", { defaultValue: "Upload failed. Try another photo." }));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  const trigger = () => (onPick ? onPick({ upload: () => inputRef.current?.click() }) : inputRef.current?.click());

  return (
    <div className={cn("tw-flex tw-flex-col tw-gap-1.5", className)}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFile(e.dataTransfer.files?.[0]);
        }}
        className={cn("tw-group tw-relative tw-overflow-hidden tw-transition-colors", value ? "" : "cs-grid-bg", dragging && "tw-ring-2 tw-ring-ring/40", round ? "tw-h-24 tw-w-24 tw-rounded-full" : cn(aspect, "tw-w-full", R["3xl"]))}
      >
        {value ? (
          <ImageResizable src={value} size={size} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <button type="button" onClick={trigger} className="tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-1.5 tw-text-muted-foreground">
            {busy ? <LoaderCircle className="tw-size-6 tw-animate-spin" /> : <ImagePlus className="tw-size-6" strokeWidth={1.5} />}
            {!round && !compact && <span className="tw-text-sm tw-font-medium">{label || t("club_add_photo", { defaultValue: "Add a photo" })}</span>}
          </button>
        )}
        {children}
        {/* Luma: a round "change image" button pinned to the bottom-right corner. */}
        <button
          type="button"
          onClick={trigger}
          aria-label={t("club_change_photo", { defaultValue: "Change photo" })}
          className={cn("tw-absolute tw-bottom-3 tw-right-3 tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-popover tw-text-popover-foreground tw-shadow-lg tw-ring-1 tw-ring-[var(--hairline)] tw-transition hover:tw-scale-105", round && "tw-bottom-0 tw-right-0 tw-size-8", !value && "tw-hidden")}
        >
          {busy ? <LoaderCircle className="tw-size-4 tw-animate-spin" /> : <ImagePlus className="tw-size-4" />}
        </button>
        {value && !round && (
          <button type="button" aria-label={t("club_remove_photo", { defaultValue: "Remove photo" })} onClick={() => onChange(null)} className="tw-absolute tw-left-3 tw-top-3 tw-flex tw-size-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-black/50 tw-text-white tw-opacity-0 tw-transition group-hover:tw-opacity-100">
            <Trash2 className="tw-size-3.5" />
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => handleFile(e.target.files?.[0])} />
      {error && <p className="tw-text-[13px] tw-font-medium tw-text-destructive">{error}</p>}
    </div>
  );
};

export const CloseButton = ({ onClick, label }) => (
  <Button variant="ghost" size="icon" aria-label={label} onClick={onClick}><X /></Button>
);

// -------------------------------------------------------------- Formatting

export const formatMoney = (amount, currency, { free, signed } = {}) => {
  const n = Number(amount || 0);
  if (n === 0 && free) return free;
  const code = String(currency || "sgd").toUpperCase();
  const abs = Math.abs(n);
  const digits = Number.isInteger(abs) ? 0 : 2;
  const body = `${code} ${abs.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: 2 })}`;
  if (signed) return `${n < 0 ? "−" : "+"}${body}`;
  return n < 0 ? `−${body}` : body;
};

export const formatEventDate = (date, time) => {
  if (!date) return "";
  const day = clubDate(date, "shortYear");
  if (!time) return day;
  return `${day} · ${clubTime(time)}`;
};

export const formatTime = (time) => clubTime(time);

// "Today" / "Tomorrow" / "Sat, 27 Sep"
export const relativeDay = (date, t) => {
  const d = dayjs(date).startOf("day");
  const today = dayjs().startOf("day");
  const diff = d.diff(today, "day");
  if (diff === 0) return t("club_today", { defaultValue: "Today" });
  if (diff === 1) return t("club_tomorrow", { defaultValue: "Tomorrow" });
  if (diff === -1) return t("club_yesterday", { defaultValue: "Yesterday" });
  return clubFormat(d, d.year() === today.year() ? "short" : "dayMonthYear");
};

export const addMinutesToTime = (time, minutes) => {
  if (!time) return "";
  const [hh, mm] = String(time).split(":").map((x) => parseInt(x, 10));
  const total = ((hh * 60 + (mm || 0) + Number(minutes || 0)) % 1440 + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};
export const minutesBetween = (start, end) => {
  if (!start || !end) return null;
  const toMin = (s) => {
    const [h, m] = String(s).split(":").map((x) => parseInt(x, 10));
    return h * 60 + (m || 0);
  };
  let diff = toMin(end) - toMin(start);
  if (diff <= 0) diff += 1440;
  return diff;
};
export const durationLabel = (minutes, t) => {
  const m = Number(minutes || 0);
  if (!m) return "";
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (h && rest) return t("club_duration_hours_minutes", { hours: h, minutes: rest, defaultValue: "{{hours}}h {{minutes}}m" });
  if (h) return t("club_duration_hours", { count: h, defaultValue: "{{count}}h" });
  return t("club_duration_minutes", { count: rest, defaultValue: "{{count}} min" });
};

// Google Maps deep link for a stored location (works without any API key).
export const mapsLink = (loc) => {
  if (!loc) return null;
  const q = loc.lat != null && loc.lng != null ? `${loc.lat},${loc.lng}` : [loc.name, loc.address].filter(Boolean).join(", ");
  if (!q) return null;
  const params = new URLSearchParams({ api: "1", query: q });
  if (loc.place_id && !String(loc.place_id).startsWith("osm:")) params.set("query_place_id", loc.place_id);
  return `https://www.google.com/maps/search/?${params.toString()}`;
};
